//jan20 TrialBanner.jsx
/* ------------------------------------------------------------------
 * src/components/Billing/TrialBanner.jsx
 * ------------------------------------------------------------------
 * Conversion-optimised trial banner
 *   • Dynamic: trial length comes from Stripe (no hard-coding)
 *   • Neutral surface + coloured left border
 *   • Light **tint background when severity = warning | error**
 *   • Count-down value bold + tinted with the same urgency colour
 *   • Subtle text-style “Upgrade” CTA
 *   • Dismiss (X) icon anchored hard-right
 *   • Always re-appears during the final 48 h
 * ------------------------------------------------------------------*/

import React, { useState } from "react";
import dayjs               from "dayjs";
import {
  Alert,
  Button,
  IconButton,
  Stack,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  alpha,                /*  ← for translucent tint */
} from "@mui/material";
import CloseIcon           from "@mui/icons-material/Close";
import { useBillings }     from "../../contexts/BillingContext";
import { useAuth }         from "../../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";

export default function TrialBanner() {
  /* ───────────────────── context & hooks (ALWAYS called) ───────────── */
  /**
   * TrialBanner also renders on pages that might NOT be wrapped in
   * <BillingsProvider> (e.g. public/anon views).  In that case
   * `useBillings()` simply returns **undefined**.  We therefore:
   *   1️⃣ call the hook unconditionally (keeps React-Hooks order ✅)
   *   2️⃣ read everything via optional-chaining with safe fall-backs
   */
  const billingsCtx            = useBillings() || {};          // may be {}

  const currentSubscription    = billingsCtx.currentSubscription ?? null;
  const createCheckoutSession  = billingsCtx.createCheckoutSession
                                  ?? (async () => null);       // inert fallback
  const setShowNavUpgrade      = billingsCtx.setShowNavUpgrade
                                  ?? (() => {});               // no-op fallback

  const { hasPermission }  = useAuth();
  const canPay             = hasPermission("manageBilling");

  const nav       = useNavigate();
  const location  = useLocation();
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down("sm"));

  /* ───────────────────── dismiss state (unconditional) ─────────────── */
  const key = "trialBannerDismissed";
  const [dismissed, setDismissed] = useState(
    sessionStorage.getItem(key) === "true",
  );

  /* ───────────────────────── guard ─────────────────────────────────── */
  if (currentSubscription?.status !== "trialing") return null;

  /* ─────────────────────── trial maths ─────────────────────────────── */
  const trialStart = dayjs(currentSubscription.trialStartsAt); // may be null
  const trialEnd   = dayjs(currentSubscription.trialEndsAt);

  /* Fallback when trialStartsAt missing (old data) → assume 14-day trial */
  const TOTAL_DAYS = trialStart.isValid()
    ? Math.max(1, trialEnd.diff(trialStart, "day"))
    : 14;

const daysLeft = Math.max(
  0,
  Math.ceil(trialEnd.diff(dayjs(), "hour", true) / 24)
);

  /* ───────────────────── colour escalation ─────────────────────────── */
  const warnCutoff = Math.ceil(TOTAL_DAYS * 0.40);
  const critCutoff = Math.ceil(TOTAL_DAYS * 0.15);

  const severity =
    daysLeft > warnCutoff ? "info"
    : daysLeft > critCutoff ? "warning"
    : "error";

  /* Map severity → palette colour (for border & numeric tint) */
  const borderColor =
    severity === "error"
      ? theme.palette.error.main
      : severity === "warning"
      ? theme.palette.warning.main
      : theme.palette.info.main;

  const numberTint =
    severity === "error"
      ? theme.palette.error.main
      : severity === "warning"
      ? theme.palette.warning.dark
      : "inherit";

  /* ─── NEW: background tint only for warning / error ─────────────── */
  const bgColor =
    severity === "info"
      ? theme.palette.background.paper                       /* neutral */
      : theme.palette.mode === "light"
          /* ~8 % opacity on light surfaces */
          ? alpha(theme.palette[severity].main, 0.08)
          /* ~25 % opacity in dark mode for legibility */
          : alpha(theme.palette[severity].dark, 0.25);

//  console.log("[TrialBanner] trial info →", {
//    trialStart : trialStart.toISOString(),
//    trialEnd   : trialEnd.toISOString(),
//    TOTAL_DAYS,
//    daysLeft,
//    severity,
//    borderColor,
//    bgColor,
//  });

  /* ───────────────────── dismiss logic ─────────────────────────────── */
  if (daysLeft <= 2) sessionStorage.removeItem(key);   // force show final 48 h
  if (dismissed && daysLeft > 2) return null;

  /* ───────────────────── CTA handler ───────────────────────────────── */
const handleUpgrade = () => {
  nav("/subscription", {
    state: {
      from: location.pathname,
      intent: "upgrade",   // optional, for analytics/UI behavior
    },
  });
};


  /* ─────────────────────── render ──────────────────────────────────── */
  return (
    <Alert
      severity={severity}
      icon={false}               /* cleaner visual – we handle colours */
      sx={{
        borderRadius   : 0,
        position       : "sticky",
        top            : 0,
        zIndex         : theme.zIndex.appBar + 1,
        py             : 0.75,
        px             : 2,
        backgroundColor: bgColor,              /* ← tinted when needed */
        borderLeft     : `4px solid ${borderColor}`,
        "& .MuiAlert-message": { width: "100%" },
      }}
    >
      {/* OUTER flex row: centred cluster + icon hard-right */}
      <Stack
        direction="row"
        alignItems="center"
        width="100%"
      >
        {/* ── centred copy + subtle CTA ─────────────────────────────── */}
        <Stack
          direction={isMobile ? "column" : "row"}
          alignItems="center"
          spacing={1}
          sx={{ mx: "auto" }}   /* centre cluster */
        >
          <Typography
            variant="body2"
            fontWeight={500}     /* baseline weight */
            textAlign={isMobile ? "center" : "left"}
          >
            {"Your free trial ends in "}
            <Box
              component="span"
              sx={{ fontWeight: 700, color: numberTint }}
            >
              {daysLeft} day{daysLeft === 1 ? "" : "s"}
            </Box>
            .
          </Typography>

          {daysLeft > 0 && canPay && (
            <Button
              variant="text"      /* looks like a link */
              size="small"
              onClick={handleUpgrade}
              sx={(th) => ({
                fontWeight          : 600,
                textDecoration      : "underline",
                textUnderlineOffset : "2px",
                minWidth            : "auto",
                px                  : 0.5,
                color               : th.palette.primary.main,
                transition          : "color 0.15s ease",
                "&:hover": {
                  backgroundColor : "transparent",
                  textDecoration  : "underline",         // keep underline
                  color           : th.palette.primary.dark,
                },
              })}
            >
              Upgrade
            </Button>
          )}
        </Stack>

        {/* ── dismiss icon fixed to far right ───────────────────────── */}
        {daysLeft > 2 && (
          <IconButton
            size="small"
            aria-label="dismiss"
            onClick={() => {
              sessionStorage.setItem(key, "true");
              setDismissed(true);
              setShowNavUpgrade(true);
            }}
            sx={{ ml: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Alert>
  );
}
